import {
  S3Client,
  PutObjectCommand,
  DeleteObjectCommand,
  ObjectCannedACL,
  CopyObjectCommand,
} from "@aws-sdk/client-s3";
import { v4 as uuidv4 } from "uuid";
import imageCompression from "browser-image-compression";
import { DecriptEnv } from "./utils";

// 1. Initialize Client with Decrypted Keys
const s3Client = new S3Client({
  region: DecriptEnv(process.env.REACT_APP_AWS_REGION),
  credentials: {
    // DecriptEnv handles undefined by returning "", so we pass the env var directly
    accessKeyId: DecriptEnv(process.env.REACT_APP_AWS_ACCESS_KEY_ID),
    secretAccessKey: DecriptEnv(process.env.REACT_APP_AWS_SECRET_ACCESS_KEY),
  },
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

// --- HELPER: Compression Logic ---
const compressFile = async (file: File, isThumbnail: boolean) => {
  // Standard: Max 1MB, 1920px wide
  const standardOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.8,
  };

  // Thumbnail: Max 50KB, 300px wide
  const thumbOptions = {
    maxSizeMB: 0.05,
    maxWidthOrHeight: 300,
    useWebWorker: true,
    initialQuality: 0.6,
  };

  try {
    return await imageCompression(
      file,
      isThumbnail ? thumbOptions : standardOptions
    );
  } catch (error) {
    console.warn("Compression failed, using original:", error);
    return file;
  }
};

export interface UploadResult {
  Location: string;
  Key: string;
  Bucket: string;
}

// --- MAIN FUNCTION: Dual Upload ---
export const uploadWithThumbnail = async (
  file: File,
  bucketName: string,
  folder: string = "uploads",
  generateThumbnail: boolean = true
): Promise<UploadResult> => {
  // 1. Generate ONE shared filename
  const cleanFileName = file.name.replace(/\s+/g, "_");
  const uniqueId = uuidv4();
  const fileName = `${uniqueId}_${cleanFileName}`;

  // 2. Define Paths
  const mainKey = `${folder}/${fileName}`;

  try {
    // 3. Prepare Main File (Always happens)
    const mainFile = await compressFile(file, false);
    const uploadMain = new PutObjectCommand({
      Bucket: bucketName,
      Key: mainKey,
      Body: mainFile,
      ContentType: file.type,
      ACL: "public-read" as ObjectCannedACL,
    });

    // Start with main upload
    const uploadPromises = [s3Client.send(uploadMain)];

    // 4. Conditionally Prepare Thumbnail
    if (generateThumbnail) {
      const thumbKey = `thumbnails/${fileName}`;
      const thumbFile = await compressFile(file, true);

      const uploadThumb = new PutObjectCommand({
        Bucket: bucketName,
        Key: thumbKey,
        Body: thumbFile,
        ContentType: file.type,
        ACL: "public-read" as ObjectCannedACL,
      });

      uploadPromises.push(s3Client.send(uploadThumb));
    }

    // 5. Execute Concurrently
    await Promise.all(uploadPromises);

    // 6. Return ONLY Main Image Details
    // 👇 Decrypt Region here to construct the correct URL
    const region = DecriptEnv(process.env.REACT_APP_AWS_REGION);
    const location = `https://${bucketName}.s3.${region}.amazonaws.com/${mainKey}`;

    return {
      Location: location,
      Key: mainKey,
      Bucket: bucketName,
    };
  } catch (error) {
    console.error("Upload Error:", error);
    throw error;
  }
};

export const uploadFileToS3 = async (
  file: File,
  bucketName: string,
  folder: string = "documents",
): Promise<UploadResult> => {
  // 1. Generate unique filename
  const cleanFileName = file.name.replace(/\s+/g, "_");
  const uniqueId = uuidv4();
  const fileName = `${uniqueId}_${cleanFileName}`;

  // 2. Define Path
  const fileKey = `${folder}/${fileName}`;

  try {
    // 3. Prepare Command (No compression for generic files)
    const command = new PutObjectCommand({
      Bucket: bucketName,
      Key: fileKey,
      Body: file,
      ContentType: file.type,
      ACL: "public-read" as ObjectCannedACL,
    });

    // 4. Execute Upload
    await s3Client.send(command);

    // 5. Return Details
    const region = DecriptEnv(process.env.REACT_APP_AWS_REGION);
    const location = `https://${bucketName}.s3.${region}.amazonaws.com/${fileKey}`;

    return {
      Location: location,
      Key: fileKey,
      Bucket: bucketName,
    };
  } catch (error) {
    console.error("Generic File Upload Error:", error);
    throw error;
  }
};

export const deleteFromS3 = async (keyOrUrl: string) => {
  if (!keyOrUrl) return;

  let cleanKey = keyOrUrl;
  try {
    if (keyOrUrl.startsWith("http://") || keyOrUrl.startsWith("https://")) {
      const urlObj = new URL(keyOrUrl);
      cleanKey = urlObj.pathname.substring(1);
    } else if (cleanKey.startsWith("/")) {
      cleanKey = cleanKey.substring(1);
    }
  } catch (e) {
    cleanKey = keyOrUrl;
  }

  if (!cleanKey) return;

  const params = {
    // 👇 Decrypt Bucket Name here
    Bucket: DecriptEnv(process.env.REACT_APP_AWS_BUCKET_NAME),
    Key: cleanKey,
  };

  try {
    const command = new DeleteObjectCommand(params);
    return await s3Client.send(command);
  } catch (error) {
    console.error("S3 Delete Error:", error);
    throw error;
  }
};

export const copyS3Image = async (sourceKey: string): Promise<string> => {
  if (!sourceKey) return "";

  const bucketName = DecriptEnv(process.env.REACT_APP_AWS_BUCKET_NAME);

  // 1. Generate New Unique Filename
  const uniqueId = uuidv4();
  // Assumes key format is "folder/uuid_filename" -> extracts "filename"
  const originalFileName =
    sourceKey.split("_").slice(1).join("_") || "image.png";
  const newFileName = `${uniqueId}_${originalFileName}`;

  // 2. Define New Keys
  const newMainKey = `uploads/${newFileName}`;
  const newThumbKey = `thumbnails/${newFileName}`;

  // 3. Derive Old Thumbnail Key (assuming standard structure)
  // Extract just the filename from the source key (e.g. "uploads/abc.jpg" -> "abc.jpg")
  const sourceFileNameOnly = sourceKey.split("/").pop();
  const sourceThumbKey = `thumbnails/${sourceFileNameOnly}`;

  try {
    // 4. Copy Main Image
    const copyMain = new CopyObjectCommand({
      Bucket: bucketName,
      CopySource: `${bucketName}/${sourceKey}`, // Source must include bucket
      Key: newMainKey,
      ACL: "public-read" as ObjectCannedACL,
    });

    // 5. Copy Thumbnail (Fire and forget, or await)
    const copyThumb = new CopyObjectCommand({
      Bucket: bucketName,
      CopySource: `${bucketName}/${sourceThumbKey}`,
      Key: newThumbKey,
      ACL: "public-read" as ObjectCannedACL,
    });

    // Execute copies in parallel
    await Promise.allSettled([
      s3Client.send(copyMain),
      s3Client.send(copyThumb),
    ]);

    return newMainKey; // Return the NEW unique key
  } catch (error) {
    console.error("Copy S3 Error:", error);
    return ""; // Return empty or handle error as needed
  }
};
